import { useState, useEffect } from "react";
import {
  Dialog,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const EditAdminModal = ({ show, onClose, onSave, user, isSubmitting = false }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");

  useEffect(() => {
    if (user) {
      setEmail(user.email);
      // Roles arrive as display text ("Admin"/"Manager"/"Viewer"/"Owner");
      setRole(String(user.role || "").trim().toLowerCase() || "viewer");
    }
  }, [user]);

  const roles = [
    {
      value: "admin",
      label: "Admin",
      description: "Full access to all features and settings",
    },
    {
      value: "manager",
      label: "Manager",
      description: "Access to all except billing and account settings",
    },
    {
      value: "viewer",
      label: "Viewer",
      description: "Read-only access to logs & usage data",
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (user) {
      await onSave({ ...user, email, role });
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        },
      }}
    >
      <div className="pb-0 pr-16 px-6 pt-4">
        <h2 className="text-xl font-bold text-primary">Edit Admin Access</h2>
        <p className="text-sm text-gray-500 mt-1">
          Update admin role access
        </p>
        <IconButton
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "grey.500",
          }}
        >
          <CloseIcon />
        </IconButton>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="pt-6 px-6">
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            fullWidth
            required
            disabled
            helperText="Email updates are not supported by this API endpoint."
            variant="outlined"
            sx={{ mb: 3 }}
          />

          <FormControl component="fieldset">
            <FormLabel
              component="legend"
              sx={{ fontWeight: 500, color: "text.primary", mb: 1 }}
            >
              Role
            </FormLabel>
            <RadioGroup value={role} onChange={(e) => setRole(e.target.value)}>
              {roles.map((roleOption) => (
                <div key={roleOption.value} className="mb-2">
                  <FormControlLabel
                    value={roleOption.value}
                    control={
                      <Radio
                        sx={{
                          color: "grey.400",
                          "&.Mui-checked": {
                            color: "#0A2342",
                          },
                        }}
                      />
                    }
                    label={
                      <div>
                        <span className="font-medium text-gray-900">
                          {roleOption.label}
                        </span>
                        <p className="text-sm text-gray-500">
                          {roleOption.description}
                        </p>
                      </div>
                    }
                  />
                </div>
              ))}
            </RadioGroup>
          </FormControl>
        </div>

        <div className="flex justify-end gap-3 px-6 pb-4">
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              bgcolor: "#0A2342",
              "&:hover": { bgcolor: "#0A2342" },
              textTransform: "uppercase",
              fontWeight: 600,
              px: 3,
            }}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={isSubmitting}
            sx={{
              borderColor: "grey.300",
              color: "text.primary",
              textTransform: "uppercase",
              fontWeight: 600,
              px: 3,
              "&:hover": { borderColor: "grey.400", bgcolor: "grey.50" },
            }}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

export default EditAdminModal;
